import { costOfLiving } from '../../../mocks/relocation';

export default function CostOfLivingSection() {
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return { icon: 'ri-arrow-up-s-line', color: 'text-red-500' };
      case 'down': return { icon: 'ri-arrow-down-s-line', color: 'text-emerald-500' };
      default: return { icon: 'ri-subtract-line', color: 'text-gray-400' };
    }
  };

  return (
    <section id="cost-of-living" className="py-20 px-6 scroll-mt-32">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-teal-700 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            <i className="ri-money-dollar-circle-line"></i>
            Financial Planning
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Cost of Living Comparison</h2>
          <p className="text-gray-500 max-w-xl mx-auto text-sm">
            Roseville offers a more affordable lifestyle than most California cities while maintaining excellent quality of life and services.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-5 gap-4 px-6 py-4 bg-gray-50 border-b border-gray-100">
            <div className="col-span-2 text-sm font-semibold text-gray-700">Category</div>
            <div className="text-sm font-semibold text-teal-700 text-center">Roseville</div>
            <div className="text-sm font-semibold text-gray-500 text-center">California Avg</div>
            <div className="text-sm font-semibold text-gray-500 text-center">National Avg</div>
          </div>

          {/* Rows */}
          {costOfLiving.map((item, index) => {
            const trend = getTrendIcon(item.trend);
            return (
              <div
                key={item.category}
                className={`grid grid-cols-5 gap-4 px-6 py-5 items-center ${
                  index < costOfLiving.length - 1 ? 'border-b border-gray-50' : ''
                } hover:bg-gray-50/50 transition-colors`}
              >
                <div className="col-span-2 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50">
                    <i className={`${item.icon} text-teal-600 text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.category}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <i className={`${trend.icon} ${trend.color} text-sm`}></i>
                      <span className="text-xs text-gray-400 capitalize">{item.trend}</span>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <span className="text-sm font-bold text-teal-700">{item.roseville}</span>
                </div>
                <div className="text-center">
                  <span className="text-sm text-gray-500">{item.california}</span>
                </div>
                <div className="text-center">
                  <span className="text-sm text-gray-500">{item.national}</span>
                </div>
              </div>
            );
          })}
          
          {/* Source Attribution */}
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              Data provided by U.S. Bureau of Labor Statistics, Zillow, Numbeo, and Council for Community and Economic Research
            </p>
            <p className="text-[11px] text-gray-400 text-center mt-1">
              <i className="ri-time-line mr-1"></i>Data last updated: December 20, 2024
            </p>
          </div>
        </div>

        {/* Data Sources Note */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400">
            Sources: {costOfLiving.map((item, i) => (
              <span key={item.category}>
                {item.source}{i < costOfLiving.length - 1 ? ', ' : ''}
              </span>
            ))}
          </p>
        </div>

        {/* Highlight Cards */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <div className="bg-emerald-50 rounded-xl p-5">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-100 mb-3">
              <i className="ri-flashlight-line text-emerald-600 text-lg"></i>
            </div>
            <h4 className="font-semibold text-gray-900 text-sm mb-1">
              <a href="#utilities" className="hover:text-emerald-700 cursor-pointer">City-Owned Utilities</a>
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed">Roseville&apos;s municipal electric utility offers rates <strong>30-40% lower</strong> than PG&amp;E, saving residents hundreds annually.<sup className="text-emerald-600 ml-0.5" title="Source: Roseville Electric Utility">*</sup></p>
          </div>
          <div className="bg-amber-50 rounded-xl p-5">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100 mb-3">
              <i className="ri-home-heart-line text-amber-600 text-lg"></i>
            </div>
            <h4 className="font-semibold text-gray-900 text-sm mb-1">
              <a href="#neighborhoods" className="hover:text-amber-700 cursor-pointer">Housing Value</a>
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed">Median home prices are <strong>22% below</strong> the California average while offering newer construction and larger lots.<sup className="text-amber-600 ml-0.5" title="Source: Zillow 2024">*</sup></p>
          </div>
          <div className="bg-sky-50 rounded-xl p-5">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-sky-100 mb-3">
              <i className="ri-briefcase-line text-sky-600 text-lg"></i>
            </div>
            <h4 className="font-semibold text-gray-900 text-sm mb-1">Strong Job Market</h4>
            <p className="text-xs text-gray-600 leading-relaxed">Major employers include Kaiser, Sutter Health, HP, Oracle, and the State of California — <strong>3.8% unemployment rate</strong>.<sup className="text-sky-600 ml-0.5" title="Source: Bureau of Labor Statistics 2024">*</sup></p>
          </div>
        </div>
      </div>
    </section>
  );
}
